import React, { useState } from 'react';
import { DATE_RANGES, FINANCIAL_RANGES, PERIOD_UNITS, formatPeriod, isPresetPeriod, parsePeriod, periodLabel } from '../../utils/cubeQuery';
import './PeriodSelect.css';

const CUSTOM = '__custom';
const UNIT_CURRENT = { day: 'today', week: 'this week', month: 'this month', quarter: 'this quarter', year: 'this year' };
const pad = (n) => String(n).padStart(2, '0');
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const monthsAgoIso = (n) => { const d = new Date(); d.setMonth(d.getMonth() - n); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };

/** The fields of the custom editor for a value, or a starting point when it is not a custom period. */
function draftFor(value) {
  const parsed = parsePeriod(value);
  return {
    kind: parsed?.kind || 'last',
    count: parsed?.kind === 'last' ? String(parsed.count) : '6',
    unit: parsed?.kind === 'last' ? parsed.unit : 'month',
    current: parsed?.kind === 'last' ? parsed.current : false,
    start: parsed?.start || monthsAgoIso(6),
    end: parsed?.end || todayIso(),
  };
}

/** The period a draft describes, or an explanation of why it is not complete. */
function periodFrom(draft) {
  if (draft.kind === 'last') {
    const count = Number(draft.count);
    if (!Number.isInteger(count) || count < 1 || count > 1000) return { problem: 'Enter a number from 1 to 1000.' };
    return { value: formatPeriod({ kind: 'last', count, unit: draft.unit, current: draft.current }) };
  }
  if (!draft.start) return { problem: 'Choose a start date.' };
  if (draft.kind === 'since') {
    if (draft.start > todayIso()) return { problem: 'The start date is in the future.' };
    return { value: formatPeriod({ kind: 'since', start: draft.start }) };
  }
  if (!draft.end) return { problem: 'Choose an end date.' };
  if (draft.start > draft.end) return { problem: 'The end date is before the start date.' };
  return { value: formatPeriod({ kind: 'between', start: draft.start, end: draft.end }) };
}

const presetOptions = (list) => list.filter((r) => r.value).map((r) => <option key={r.value} value={r.value}>{r.label}</option>);

/**
 * A period dropdown: the listed periods, plus "Custom period…", which opens
 * fields for the last N days to years (complete, or including the current
 * one), since a date, or between two dates.
 *
 * emptyLabel names the blank choice (null leaves it out); ranges is
 * 'calendar', 'calendar-financial' or 'financial-calendar'.
 */
const PeriodSelect = ({ value, onChange, ariaLabel, emptyLabel = null, ranges = 'calendar', disabled = false, title }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => draftFor(value));
  const custom = !isPresetPeriod(value);
  const { problem } = periodFrom(draft);

  const change = (patch) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    const result = periodFrom(next);
    if (result.value && result.value !== value) onChange(result.value);
  };

  const choose = (e) => {
    if (e.target.value !== CUSTOM) { setEditing(false); onChange(e.target.value); return; }
    const start = draftFor(value);
    setDraft(start);
    setEditing(true);
    const result = periodFrom(start);
    if (result.value && result.value !== value) onChange(result.value);
  };

  const calendar = <optgroup label="Calendar">{presetOptions(DATE_RANGES)}</optgroup>;
  const financial = <optgroup label="Financial year">{presetOptions(FINANCIAL_RANGES)}</optgroup>;
  const name = ariaLabel || 'Period';

  return (
    <div className={`period-select ${editing ? 'period-select--editing' : ''}`}>
      <div className="period-select-row">
        <select aria-label={ariaLabel} value={custom ? value : (value || '')} onChange={choose} disabled={disabled} title={title}>
          {emptyLabel !== null && <option value="">{emptyLabel}</option>}
          {ranges === 'calendar' && presetOptions(DATE_RANGES)}
          {ranges === 'calendar-financial' && <>{calendar}{financial}</>}
          {ranges === 'financial-calendar' && <>{financial}{calendar}</>}
          <optgroup label="Custom">
            {custom && <option value={value}>{periodLabel(value)}</option>}
            <option value={CUSTOM}>Custom period…</option>
          </optgroup>
        </select>
        {custom && !editing && !disabled && (
          <button type="button" className="period-select-edit" onClick={() => { setDraft(draftFor(value)); setEditing(true); }}
            aria-label={`${name}: change custom period`}>Change</button>
        )}
      </div>

      {editing && (
        <div className="period-custom" role="group" aria-label={`${name}: custom period`}>
          <select aria-label="Custom period type" value={draft.kind} onChange={(e) => change({ kind: e.target.value })}>
            <option value="last">In the last</option>
            <option value="between">Between</option>
            <option value="since">Since</option>
          </select>

          {draft.kind === 'last' && (
            <>
              <span className="period-custom-pair">
                <input type="number" min={1} max={1000} step={1} aria-label="Number of periods" value={draft.count}
                  onChange={(e) => change({ count: e.target.value })} />
                <select aria-label="Unit" value={draft.unit} onChange={(e) => change({ unit: e.target.value })}>
                  {PERIOD_UNITS.map((u) => <option key={u} value={u}>{`${u}s`}</option>)}
                </select>
              </span>
              <label className="period-custom-check">
                <input type="checkbox" checked={draft.current} onChange={(e) => change({ current: e.target.checked })} />
                <span>{`Include ${UNIT_CURRENT[draft.unit]}`}</span>
              </label>
            </>
          )}

          {draft.kind !== 'last' && (
            <span className="period-custom-pair">
              <input type="date" aria-label="Start date" value={draft.start} max={draft.kind === 'since' ? todayIso() : undefined}
                onChange={(e) => change({ start: e.target.value })} />
              {draft.kind === 'between' && (
                <>
                  <span>and</span>
                  <input type="date" aria-label="End date" value={draft.end} min={draft.start || undefined}
                    onChange={(e) => change({ end: e.target.value })} />
                </>
              )}
            </span>
          )}

          <p className={`period-custom-note ${problem ? 'period-custom-note--problem' : ''}`} role={problem ? 'alert' : undefined}>
            {problem || (draft.kind === 'last' && !draft.current
              ? `Complete ${draft.unit}s only, not ${UNIT_CURRENT[draft.unit]}.`
              : periodLabel(periodFrom(draft).value))}
          </p>
          <button type="button" className="period-custom-done" onClick={() => setEditing(false)}>Done</button>
        </div>
      )}
    </div>
  );
};

export default PeriodSelect;
